import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { getTasksForTrainee, submitTask } from '../../api_service';

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: 'text-warning',  bg: 'bg-warning/10',  icon: 'Clock'       },
  SUBMITTED: { label: 'Submitted', color: 'text-primary',  bg: 'bg-primary/10',  icon: 'Send'        },
  REVIEWED:  { label: 'Reviewed',  color: 'text-success',  bg: 'bg-success/10',  icon: 'CheckCircle' },
};

const SubmitModal = ({ item, traineeId, onClose, onSubmitted }) => {
  const [text, setText] = useState(item.submission?.submissionText || '');
  const [fileUrl, setFileUrl] = useState(item.submission?.fileUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!text.trim()) { setError('Please write your submission before submitting'); return; }
    setLoading(true);
    setError('');
    try {
      await submitTask(item.task.taskId, traineeId, { submissionText: text, fileUrl });
      onSubmitted();
      onClose();
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Submit Task</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{item.task?.title}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Your Work *</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={6}
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Describe what you did, paste your solution, or summarize your findings..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">File / Link (optional)</label>
            <input
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              value={fileUrl}
              onChange={e => setFileUrl(e.target.value)}
              placeholder="Paste a URL or file link..."
            />
          </div>
          {error && <p className="text-sm text-error">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const MyTasks = () => {
  const navigate = useNavigate();
  const traineeId = sessionStorage.getItem('userId');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitTarget, setSubmitTarget] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadTasks(); }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await getTasksForTrainee(traineeId);
      setTasks(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error bg-error/10' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning bg-warning/10' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning bg-warning/10' };
    return { text: `${diff} days left`, cls: 'text-muted-foreground bg-muted/40' };
  };

  const filtered = tasks.filter(item => {
    if (filter === 'all') return true;
    return item.submission?.status === filter || (!item.submission && filter === 'PENDING');
  });

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="trainee"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">My Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">View and submit your assigned tasks</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { key: 'all',       label: 'Total',     icon: 'ClipboardList', color: 'text-primary'  },
            { key: 'PENDING',   label: 'Pending',   icon: 'Clock',         color: 'text-warning'  },
            { key: 'SUBMITTED', label: 'Submitted', icon: 'Send',          color: 'text-primary'  },
          ].map(s => {
            const count = s.key === 'all' ? tasks.length
              : tasks.filter(t => (t.submission?.status || 'PENDING') === s.key).length;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`bg-card border rounded-xl p-4 text-left transition-all ${filter === s.key ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/40'}`}
              >
                <Icon name={s.icon} size={20} className={`${s.color} mb-2`} />
                <p className="text-2xl font-bold text-foreground">{count}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </button>
            );
          })}
        </div>

        {/* Task list */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading tasks...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No tasks found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(item => {
              const status = item.submission?.status || 'PENDING';
              const cfg = STATUS_CONFIG[status];
              const dl = deadlineLabel(item.task?.deadline);
              return (
                <div key={item.task?.taskId} className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-foreground">{item.task?.title}</h3>
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {dl && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${dl.cls}`}>{dl.text}</span>
                        )}
                      </div>
                      {item.task?.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.task.description}</p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Deadline: {item.task?.deadline || 'None'}</span>
                        <span>Max Score: {item.task?.maxScore}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {status === 'PENDING' && (
                        <Button size="sm" onClick={() => setSubmitTarget(item)} iconName="Send" iconSize={14}>
                          Submit
                        </Button>
                      )}
                      {status === 'SUBMITTED' && (
                        <Button size="sm" variant="outline" onClick={() => setSubmitTarget(item)}>
                          Edit
                        </Button>
                      )}
                      {status === 'REVIEWED' && (
                        <span className="text-2xl font-bold text-success">
                          {item.submission?.score}/{item.task?.maxScore}
                        </span>
                      )}
                    </div>
                  </div>

                  {status === 'REVIEWED' && item.submission?.mentorRemarks && (
                    <div className="mt-3 bg-success/5 border border-success/20 rounded-lg p-3 text-sm text-foreground">
                      <span className="font-medium text-success">Mentor feedback: </span>
                      {item.submission.mentorRemarks}
                    </div>
                  )}

                  {status === 'SUBMITTED' && item.submission?.submissionText && (
                    <div className="mt-3 bg-muted/40 rounded-lg p-3 text-sm text-muted-foreground line-clamp-2">
                      {item.submission.submissionText}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {submitTarget && (
        <SubmitModal
          item={submitTarget}
          traineeId={traineeId}
          onClose={() => setSubmitTarget(null)}
          onSubmitted={loadTasks}
        />
      )}
    </div>
  );
};

export default MyTasks;
